import React from "react";

export default function Aup() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Acceptable Use Policy</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Purpose</h2>
        <p className="text-gray-700 mb-4">
          This Acceptable Use Policy ("AUP") sets out the rules for using the VSENDS platform. It is designed to protect V CAN RESOURCES LIMITED, our users, and the integrity of our Service. By using the Service, you agree to comply with this AUP.
        </p>
        <p className="text-gray-700 mb-4">
          This AUP forms part of our Terms of Service. Any violation of this AUP may result in suspension or termination of your Account.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. General Use</h2>
        <p className="text-gray-700 mb-4">
          You may use the Service only for lawful purposes and in accordance with this AUP. You agree not to use the Service in any way that could damage, disable, overburden, or impair our servers or networks, or interfere with any other party's use and enjoyment of the Service.
        </p>
        <p className="text-gray-700 mb-4">
          You are responsible for all activity that occurs under your Account, whether authorised by you or not. You must take reasonable steps to secure your Account credentials and notify us immediately of any unauthorised access.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. Prohibited Conduct</h2>
        <p className="text-gray-700 mb-4">
          You must not use the Service to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Send unsolicited or unauthorised advertising, junk mail, or spam.</li>
          <li>Distribute malware, viruses, worms, trojans, or other harmful code.</li>
          <li>Engage in phishing, spoofing, or any form of deceptive practice.</li>
          <li>Harass, abuse, threaten, or defame any individual or organisation.</li>
          <li>Transmit material that is obscene, pornographic, or otherwise objectionable.</li>
          <li>Infringe the intellectual property rights, privacy rights, or any other rights of any third party.</li>
          <li>Violate any applicable law, regulation, or contractual obligation.</li>
          <li>Attempt to gain unauthorised access to any part of the Service or any other system or network.</li>
          <li>Use automated means to access the Service except through our public API.</li>
          <li>Interfere with or disrupt the integrity or performance of the Service.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Email Sending Restrictions</h2>
        <p className="text-gray-700 mb-4">
          In addition to our Anti-Spam Policy, the following restrictions apply to all email sending activity:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>You must maintain a complaint rate below 0.1% (1 complaint per 1,000 emails).</li>
          <li>You must maintain a hard bounce rate below 5%.</li>
          <li>You must not send emails to recipients who have not opted in to receive communications from you.</li>
          <li>You must honour all unsubscribe requests within 10 business days.</li>
          <li>You must not send emails containing prohibited content as defined in our Terms of Service.</li>
          <li>You must not exceed the sending limits associated with your subscription plan.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Content Restrictions</h2>
        <p className="text-gray-700 mb-4">
          The following types of content are prohibited on the Service:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Content that promotes illegal activities or instructs on how to commit illegal acts.</li>
          <li>Content that contains or promotes hate speech, discrimination, or violence.</li>
          <li>Content that infringes on trademarks, copyrights, patents, or trade secrets.</li>
          <li>Content that contains false or misleading information intended to deceive recipients.</li>
          <li>Content that promotes or facilitates the sale of counterfeit goods.</li>
          <li>Content that contains or links to malicious software or websites.</li>
          <li>Content that violates the privacy rights of any individual.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. API Usage</h2>
        <p className="text-gray-700 mb-4">
          If you access the Service through our API, you must comply with the following:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Do not exceed the rate limits specified in your API plan.</li>
          <li>Do not use the API to scrape, harvest, or collect data about other users.</li>
          <li>Do not share your API keys with unauthorised third parties.</li>
          <li>Do not use the API to build a competing service or product.</li>
          <li>Do not reverse engineer, decompile, or disassemble any part of the API.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We reserve the right to modify, suspend, or discontinue the API at any time. We may also revoke API access for any user who violates this AUP.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Resource Usage</h2>
        <p className="text-gray-700 mb-4">
          You agree to use the Service's resources reasonably and not to engage in activities that consume excessive bandwidth, storage, or processing power. This includes:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Uploading excessively large files that are not related to legitimate email marketing activities.</li>
          <li>Creating an unreasonable number of campaigns, automations, or workflows.</li>
          <li>Using the Service for data storage purposes unrelated to email marketing.</li>
          <li>Any activity that places an unreasonable burden on our infrastructure.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Security Obligations</h2>
        <p className="text-gray-700 mb-4">
          You are responsible for maintaining the security of your Account and must:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Use strong, unique passwords for your Account.</li>
          <li>Enable two-factor authentication where available.</li>
          <li>Not share your login credentials with any third party.</li>
          <li>Notify us immediately if you suspect unauthorised access to your Account.</li>
          <li>Ensure that any devices used to access the Service are protected with up-to-date security software.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Enforcement</h2>
        <p className="text-gray-700 mb-4">
          We reserve the right to investigate and take appropriate action against any user who violates this AUP. Enforcement actions may include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Issuing a written warning and requiring corrective action.</li>
          <li>Temporarily suspending the Account or specific features.</li>
          <li>Permanently terminating the Account without refund.</li>
          <li>Reporting the violation to law enforcement or regulatory authorities.</li>
          <li>Pursuing legal action for damages caused by the violation.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We may act immediately to suspend or restrict access to the Service where we reasonably believe there is a serious or ongoing violation of this AUP.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Reporting Violations</h2>
        <p className="text-gray-700 mb-4">
          If you become aware of any violation of this AUP, please report it to us immediately:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Email:</strong> vg@vcanresources.com</li>
          <li><strong>Support:</strong> support@vsends.com</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Please include as much detail as possible, including the nature of the violation, the user involved (if known), and any supporting evidence. All reports will be treated confidentially.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Changes to This Policy</h2>
        <p className="text-gray-700 mb-4">
          We may update this AUP from time to time to reflect changes in our Service, technology, legal requirements, or industry standards. We will notify users of material changes by posting the updated policy on our website with a new "Last Updated" date.
        </p>
        <p className="text-gray-700 mb-4">
          Your continued use of the Service after any changes take effect constitutes your acceptance of the updated AUP. If you do not agree with the changes, you should discontinue use of the Service and terminate your Account.
        </p>
      </main>
    </div>
  );
}
